import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InvoiceList } from "./InvoiceList";
import { ExtendedInvoice } from "@/types/payment";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload } from "lucide-react";
import InvoicePopup from "@/components/orders/InvoicePopup";
import { useInvoice } from "@/hooks/useInvoice";
import { Pagination } from "@/components/ui/pagination";
import { InvoiceUploadDialog } from "@/components/dialogs/InvoiceUploadDialog";
import { adminLabels, ordersLabels } from "@/i18n";

type InvoiceManagementLabels = {
  title?: string;
  description?: string;
  uploadInvoice?: string;
  loadingInvoices?: string;
  errorLoadingInvoices?: string;
  invoicesCount?: string;
  searchPlaceholder?: string;
  search?: string;
  clear?: string;
  allStatuses?: string;
};

const InvoiceManagement: React.FC = () => {
  const { language } = useLanguage();
  const im = (adminLabels(language).economy?.invoiceManagement ??
    {}) as InvoiceManagementLabels;
  const o = ordersLabels(language);
  const { useGetAllInvoicesExtended, createInvoiceAsync, isCreatingInvoice } =
    useInvoice();

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<{
    status?: string;
    search?: string;
  }>({});
  const [shouldSearch, setShouldSearch] = useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    useState<ExtendedInvoice | null>(null);

  // Fetch extended invoices with all details and filters
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    error: invoicesError,
    refetch: refetchInvoicesData,
  } = useGetAllInvoicesExtended(
    page,
    limit,
    shouldSearch ? appliedFilters.status : undefined,
    shouldSearch ? appliedFilters.search : undefined,
  );

  const handleSearch = () => {
    const newFilters: { status?: string; search?: string } = {};

    if (filterStatus !== "all") {
      newFilters.status = filterStatus;
    }

    if (search.trim()) {
      newFilters.search = search.trim();
    }

    setAppliedFilters(newFilters);
    setShouldSearch(true);
    setPage(1); // Reset to first page when searching
  };

  const handleClearFilters = () => {
    setFilterStatus("all");
    setSearch("");
    setAppliedFilters({});
    setShouldSearch(false);
    setPage(1);
  };

  // Use server-side filtered data directly
  const filteredInvoices = invoicesData?.invoices || [];

  // Check if search button should be disabled
  const isSearchDisabled = filterStatus === "all" && !search.trim();

  const handleUploadInvoice = () => {
    setUploadOpen(true);
  };

  if (invoicesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">
          {im.loadingInvoices ?? "Laddar fakturor..."}
        </div>
      </div>
    );
  }

  if (invoicesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">
          {im.errorLoadingInvoices ??
            "Ett fel uppstod vid laddning av fakturor"}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {im.title ?? "Fakturahantering"}
            </h1>
            <p className="text-muted-foreground">
              {im.description ?? "Hantera fakturor och kontrakt"}
            </p>
          </div>
          <Button onClick={handleUploadInvoice} className="gap-2">
            <Upload className="h-4 w-4" />
            {im.uploadInvoice ?? "Ladda upp faktura"}
          </Button>
        </div>

        <Card className="glass-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle>
                {im.invoicesCount ?? "Fakturor"} ({filteredInvoices.length})
              </CardTitle>
              <div className="flex flex-col md:flex-row md:items-center gap-4 w-full md:w-auto mt-2 md:mt-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border bg-card rounded px-2 py-1 w-full md:w-auto"
                >
                  <option value="all">
                    {im.allStatuses ?? "Alla statusar"}
                  </option>
                  <option value="paid">{o.filter.paid}</option>
                  <option value="pending">{o.filter.pending}</option>
                  <option value="overdue">{o.filter.overdue}</option>
                  <option value="unpaid">{o.filter.unpaid}</option>
                </select>
                <Input
                  type="text"
                  placeholder={
                    im.searchPlaceholder ?? "Sök kund, fakturanr eller regnr"
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full md:w-64"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSearch}
                    size="sm"
                    disabled={isSearchDisabled}
                  >
                    {im.search ?? o.actions.search}
                  </Button>
                  {(appliedFilters.status || appliedFilters.search) && (
                    <Button
                      onClick={handleClearFilters}
                      variant="outline"
                      size="sm"
                    >
                      {im.clear ?? o.actions.clear}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <InvoiceList
              invoices={filteredInvoices}
              onViewInvoice={(invoice) => {
                setSelectedInvoice(invoice);
              }}
            />
            {invoicesData && (
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(invoicesData.total / limit)}
                totalItems={invoicesData.total}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={(newLimit) => {
                  setPage(1);
                }}
                itemsPerPageOptions={[10, 25, 50]}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invoice Popup */}
      {selectedInvoice && (
        <InvoicePopup
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          isAdmin={true}
          onSendInvoice={(id) =>
            alert(o.toasts?.invoiceSent ?? "Faktura skickad!")
          }
        />
      )}

      {/* Invoice Upload Dialog */}
      <InvoiceUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={() => {
          // Refresh the invoice list
          refetchInvoicesData();
        }}
      />
    </>
  );
};

export default InvoiceManagement;
